import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState } from "react";
import { MAX_STROKE_POINTS, type Pin, type StrokePoint } from "@tarkov-callout/shared";
import { assetUrl } from "../api";
import { t } from "../i18n";
import { extractsFor, spawnsFor } from "../maps";
import type { ExtractFaction, TarkovMap } from "../maps";
import { createCrs, paddedBounds, toLatLngBounds } from "./crs";
import { pinIcon } from "./pinIcon";

const SVG_NS = "http://www.w3.org/2000/svg";
/** Pointer travel, in pixels, before a placement is treated as a direction drag. */
const DRAG_THRESHOLD = 10;

/**
 * Freehand drawing.
 *
 * A hand moving across the map produces a pointer event every few milliseconds,
 * which is far more detail than a callout needs and more than the room should
 * carry. Points closer together than this are dropped as they arrive, so the
 * path is thinned while it is drawn rather than after.
 */
const STROKE_MIN_STEP = 4;

/** Rounded ends and a little simplification are what make a sampled path read as a line. */
const STROKE_STYLE = {
  weight: 4,
  opacity: 0.95,
  lineCap: "round",
  lineJoin: "round",
  smoothFactor: 1.5,
} as const;

/**
 * The width of the invisible line that actually catches the clicks.
 *
 * An SVG path is only clickable where it is painted, so a 4px stroke gives a
 * 4px target -- and a freehand line is a wobble, not a straight edge you can
 * aim at. Reported from the room as "clicking the line does not delete it".
 * A second, transparent line of the same shape carries the pointer instead.
 */
const STROKE_HIT_WEIGHT = 24;

/** How long a drawn line stays on screen unacknowledged before it is taken back. */
const STROKE_ECHO_GRACE_MS = 5_000;

export type MapStyle = "svg" | "tile";

export interface Placement {
  lat: number;
  lng: number;
  heading: number | null;
}

interface Props {
  map: TarkovMap;
  style: MapStyle;
  /** Name of the floor being shown, or null for the base artwork. */
  activeLayer: string | null;
  pins: Pin[];
  selfId: string | null;
  /** True while a pin tool is armed, which takes over pointer input. */
  placing: boolean;
  /** True while the pen is the armed tool: pointer input draws instead of placing. */
  drawing: boolean;
  /** True while the rubber is armed: pointer input takes things off the map. */
  erasing: boolean;
  /** What the pen is drawing in, so a line looks right before the server answers. */
  penColor: string;
  /** Picture-in-picture or similar: the map needs to scale down much further. */
  compact: boolean;
  /** Off by default; see maps.ts for why. */
  showExtracts: boolean;
  /** Which kinds of extract to draw; the sidebar narrows it. */
  extractFactions: ExtractFaction[];
  /** Off by default: a rough hint about where people come in, not a plan. */
  showSpawns: boolean;
  /**
   * Where to bring the view, when something outside the map asks for it.
   *
   * The list in the sidebar is how you find a pin you cannot see, so picking
   * one there has to move the map. `seq` rather than the coordinates alone:
   * asking twice for the same spot is a real request (you panned away since),
   * and identical coordinates would look like no change at all.
   */
  focus: { lat: number; lng: number; seq: number } | null;
  onPlace: (placement: Placement) => void;
  onDraw: (path: StrokePoint[]) => void;
  /** Called by the rubber, once per thing it takes off the map. */
  onErase: (id: string) => void;
  onSelectPin: (pin: Pin) => void;
  onMovePin: (id: string, lat: number, lng: number) => void;
}

interface Runtime {
  /**
   * Which build of the map this handle belongs to.
   *
   * Changing map or style tears the Leaflet instance down and builds a new one.
   * React runs that cleanup and the other effects in the same commit, so for
   * one pass the effects below still hold the *previous* runtime -- and adding
   * a marker to a map that has been removed throws inside Leaflet
   * (`_initIcon` -> appendChild of undefined), which unmounts the whole tree
   * and leaves a black rectangle. Comparing against the live generation is what
   * stops that; it depends on nothing inside Leaflet.
   */
  generation: number;
  leaflet: L.Map;
  /** Present in SVG mode; the element whose `<g>` children are the floors. */
  svgRoot: SVGSVGElement | null;
}

/**
 * Lets the map zoom out far enough to show the whole place, whatever the
 * window is.
 *
 * The per-map minimum in the data assumes a full-size pane. In a
 * picture-in-picture window that floor sits *above* the zoom at which the map
 * fits, so Leaflet clamps and the edges stay out of reach. Asking Leaflet what
 * zoom actually fits is both correct and self-adjusting -- a fixed offset would
 * be a guess that is too small on one window and absurd on another.
 */
/** The names come from upstream data, so they go through the same escaping. */
function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] ?? char,
  );
}

function isCurrent(runtime: Runtime | null, generation: number): runtime is Runtime {
  return runtime !== null && runtime.generation === generation;
}

/**
 * Cancels the frame a marker's autoPan has queued for itself.
 *
 * `autoPan` re-arms `requestAnimFrame(_adjustPan)` on every drag step, and the
 * only thing that cancels it is `dragend`. A pin that goes away mid-drag --
 * someone else deletes it, it expires, the map is switched, the rubber takes
 * it -- never gets that event, so the frame lands on a marker whose `_icon` is
 * already null and throws out of Leaflet where nothing can catch it.
 */
function stopAutoPan(marker: L.Marker): void {
  const drag = marker.dragging as unknown as { _panRequest?: number } | undefined;
  if (drag?._panRequest) L.Util.cancelAnimFrame(drag._panRequest);
}

/**
 * Which drawn line, if any, is under a point on the screen.
 *
 * The clickable copy of each stroke answers this for a plain click, but the
 * rubber never gets one: it captures the pointer so a drag keeps rubbing, and
 * a captured pointer is delivered to the container and nothing else. Same
 * question, asked in plain geometry, with the same tolerance.
 */
function strokeUnder(
  leaflet: L.Map,
  lines: Map<string, { line: L.Polyline }>,
  point: L.Point,
): string | null {
  let bestId: string | null = null;
  let bestDistance = STROKE_HIT_WEIGHT / 2;

  for (const [id, entry] of lines) {
    const latlngs = entry.line.getLatLngs() as L.LatLng[];
    for (let index = 1; index < latlngs.length; index += 1) {
      const from = latlngs[index - 1];
      const to = latlngs[index];
      if (!from || !to) continue;
      const distance = L.LineUtil.pointToSegmentDistance(
        point,
        leaflet.latLngToContainerPoint(from),
        leaflet.latLngToContainerPoint(to),
      );
      if (distance <= bestDistance) {
        bestDistance = distance;
        bestId = id;
      }
    }
  }

  return bestId;
}

function applyZoomFloor(leaflet: L.Map, map: TarkovMap, bounds: L.LatLngBounds): void {
  const fits = leaflet.getBoundsZoom(bounds, false);
  // A little past "exactly fits", so the map is not pinned to the edges.
  leaflet.setMinZoom(Math.min(map.minZoom - 1, fits - 0.5));
}

export function MapCanvas({
  map,
  style,
  activeLayer,
  pins,
  selfId,
  placing,
  drawing,
  erasing,
  penColor,
  compact,
  showExtracts,
  extractFactions,
  showSpawns,
  focus,
  onPlace,
  onDraw,
  onErase,
  onSelectPin,
  onMovePin,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const generationRef = useRef(0);

/**
 * Whether this handle is still the current one.
 *
 * Must be called from inside the effect, never during render. React renders
 * with the new props first and only then runs the previous effect's cleanup, so
 * at render time the generation has not been bumped yet and a stale runtime
 * still looks current. Reading the ref inside the effect body reads it after
 * the teardown, which is the whole point.
 */

  // Kept in refs so the imperative handlers below always see current values
  // without having to be torn down and rebuilt on every render.
  const placingRef = useRef(placing);
  const drawingRef = useRef(drawing);
  const erasingRef = useRef(erasing);
  const penColorRef = useRef(penColor);
  const onPlaceRef = useRef(onPlace);
  const onDrawRef = useRef(onDraw);
  const onEraseRef = useRef(onErase);
  const onSelectPinRef = useRef(onSelectPin);
  const onMovePinRef = useRef(onMovePin);
  /** Set while a marker is under the pointer, so an echo cannot yank it away. */
  const draggingRef = useRef<string | null>(null);
  const pinsRef = useRef(pins);
  placingRef.current = placing;
  drawingRef.current = drawing;
  erasingRef.current = erasing;
  penColorRef.current = penColor;
  onPlaceRef.current = onPlace;
  onDrawRef.current = onDraw;
  onEraseRef.current = onErase;
  onSelectPinRef.current = onSelectPin;
  onMovePinRef.current = onMovePin;
  pinsRef.current = pins;

  const markersRef = useRef(new Map<string, { marker: L.Marker; signature: string }>());
  const strokesRef = useRef(
    new Map<string, { line: L.Polyline; hit: L.Polyline; signature: string }>(),
  );
  /**
   * Lines that have been let go of but not yet come back from the server.
   *
   * The stroke is sent on release, so without this the line the hand just drew
   * would vanish for as long as the round trip takes and then reappear. Each
   * preview is dropped as our own strokes come back, oldest first, which is the
   * order they were sent in.
   */
  const pendingRef = useRef<{ line: L.Polyline; timer: number }[]>([]);
  const myStrokeCountRef = useRef(0);
  const floorTileRef = useRef<L.TileLayer | null>(null);

  /* ---------------------------------------------------------------- */
  /* Build the map. The CRS differs per map and cannot be swapped on a  */
  /* live instance, so a change of map or style rebuilds it.            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const generation = ++generationRef.current;
    const bounds = toLatLngBounds(map.bounds);
    const leaflet = L.map(container, {
      crs: createCrs(map),
      zoomControl: false,
      attributionControl: false,
      minZoom: map.minZoom - 1,
      maxZoom: map.maxZoom + (style === "svg" ? 2 : 1),
      zoomSnap: 0.25,
      wheelPxPerZoomLevel: 140,
      doubleClickZoom: false,
      maxBounds: paddedBounds(bounds, 1.6),
      maxBoundsViscosity: 0.5,
    });
    leaflet.fitBounds(bounds, { animate: false });
    applyZoomFloor(leaflet, map, bounds);

    let cancelled = false;
    markersRef.current.clear();
    strokesRef.current.clear();
    for (const pending of pendingRef.current) window.clearTimeout(pending.timer);
    pendingRef.current = [];
    myStrokeCountRef.current = 0;
    floorTileRef.current = null;

    const useTiles = style === "tile" && Boolean(map.tilePath);

    if (useTiles && map.tilePath) {
      L.tileLayer(assetUrl(map.tilePath), {
        tileSize: map.tileSize,
        minZoom: map.minZoom - 4,
        maxNativeZoom: map.maxZoom,
        bounds,
        noWrap: true,
      }).addTo(leaflet);
      setRuntime({ generation, leaflet, svgRoot: null });
    } else if (map.svgPath) {
      const svgBounds = map.svgBounds ? toLatLngBounds(map.svgBounds) : bounds;
      fetch(assetUrl(map.svgPath))
        .then((response) => response.text())
        .then((markup) => {
          if (cancelled) return;
          const host = document.createElementNS(SVG_NS, "svg");
          host.innerHTML = markup;
          const inner = host.firstElementChild;
          const viewBox = inner?.getAttribute("viewBox");
          if (viewBox) host.setAttribute("viewBox", viewBox);
          L.svgOverlay(host, svgBounds, { interactive: false }).addTo(leaflet);
          setRuntime({ generation, leaflet, svgRoot: host });
        })
        .catch(() => {
          if (!cancelled) setRuntime({ generation, leaflet, svgRoot: null });
        });
    } else {
      setRuntime({ generation, leaflet, svgRoot: null });
    }

    return () => {
      cancelled = true;
      // Invalidate before tearing down, so anything still holding this runtime
      // in the same commit sees a stale generation and leaves it alone.
      generationRef.current += 1;
      setRuntime(null);
      /*
       * Halt pans and zooms, then detach the layers, before tearing the map
       * down. A frame or a marker update that lands after remove() reads
       * _mapPane, which is gone by then, and throws out of a timer where no
       * error boundary can catch it -- so it never reaches the error boundary,
       * only the log, which is where it was found: sixty-six people a day.
       *
       * stop() alone is not enough, because Leaflet leaves two things running
       * that remove() does not take with it.
       */

      // maxBounds attaches a moveend handler that pans the view back inside the
      // bounds, and stop() fires moveend -- so stopping the animation is what
      // starts the next one, and that one outlives the map. Dropping the bounds
      // detaches the handler; this is the documented way to do it.
      leaflet.setMaxBounds(L.latLngBounds([]));
      leaflet.stop();
      // The wheel is debounced with a plain setTimeout. Leaflet keeps the
      // handle but `removeHooks()` never clears it, so a notch of scroll
      // within `wheelDebounceTime` of a switch fires _performZoom -> zoom
      // around a container point -> _getMapPanePos on a map whose _mapPane
      // has been deleted. It is the commonest of these in the log.
      clearTimeout((leaflet.scrollWheelZoom as unknown as { _timer?: number })._timer);
      // Pinch-zoom cancels its own frame on touchend and nowhere else. Switch
      // maps with two fingers still down and that frame calls _move() on the
      // torn-down map. Only ever seen from iPads, which is where pinching is.
      const pinch = leaflet.touchZoom as unknown as { _animRequest?: number };
      if (pinch?._animRequest) L.Util.cancelAnimFrame(pinch._animRequest);
      // _animateZoom arms a bare `setTimeout(_onZoomTransitionEnd, 250)` as a
      // fallback for browsers that skip transitionend, and nothing cancels it.
      // It returns early on this flag, which is the only handle on it from out
      // here. Reaching into a private field is worth it: the alternative is a
      // torn-down map calling _move() a quarter second later.
      (leaflet as unknown as { _animatingZoom: boolean })._animatingZoom = false;
      for (const entry of markersRef.current.values()) stopAutoPan(entry.marker);
      leaflet.eachLayer((layer) => leaflet.removeLayer(layer));
      markersRef.current.clear();
      strokesRef.current.clear();
      for (const pending of pendingRef.current) window.clearTimeout(pending.timer);
      pendingRef.current = [];
      myStrokeCountRef.current = 0;
      floorTileRef.current = null;
      leaflet.remove();
    };
  }, [map, style]);

  /* ---------------------------------------------------------------- */
  /* Bringing the view somewhere, on request from outside the map      */
  /* ---------------------------------------------------------------- */
  const focusedRef = useRef(0);
  useEffect(() => {
    if (!focus || focus.seq === focusedRef.current) return;
    if (!isCurrent(runtime, generationRef.current)) return;
    focusedRef.current = focus.seq;
    // Pan, not setView: the zoom someone chose is theirs, and yanking it about
    // is disorienting when all that was asked for is "show me where this is".
    runtime.leaflet.panTo([focus.lat, focus.lng]);
  }, [focus, runtime]);

  /* ---------------------------------------------------------------- */
  /* Floors                                                            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;
    const layer = map.layers.find((candidate) => candidate.name === activeLayer);

    if (live.svgRoot) {
      const root = live.svgRoot.firstElementChild ?? live.svgRoot;
      let baseGroup: Element | null = null;
      let activeGroup: Element | null = null;
      for (const child of Array.from(root.children)) {
        if (child.nodeName !== "g" || !child.id) continue;
        const isBase = child.id === map.svgLayer;
        const isActive = layer?.svgLayer !== undefined && child.id === layer.svgLayer;
        if (isBase) baseGroup = child;
        if (isActive) activeGroup = child;
        (child as SVGGElement).style.display = isBase || isActive ? "" : "none";
      }

      /*
       * A floor drawn before the base is a floor drawn underneath it.
       *
       * Factory is the one map whose file lists `Basement` above
       * `Ground_Floor`, and the ground floor's fill is opaque, so choosing
       * Tunnels used to paint the tunnels and then paint the factory back over
       * them: "the underground map is effectively invisible", as it was
       * reported. SVG has no z-index, so the fix is document order -- move the
       * chosen floor after the base and leave every other map alone.
       */
      if (
        baseGroup &&
        activeGroup &&
        activeGroup.compareDocumentPosition(baseGroup) & Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        root.insertBefore(activeGroup, baseGroup.nextSibling);
      }
    }

    floorTileRef.current?.remove();
    floorTileRef.current = null;

    if (layer?.tilePath && !live.svgRoot) {
      floorTileRef.current = L.tileLayer(assetUrl(layer.tilePath), {
        tileSize: map.tileSize,
        minZoom: map.minZoom - 4,
        maxNativeZoom: map.maxZoom,
        bounds: toLatLngBounds(map.bounds),
        noWrap: true,
        zIndex: 5,
      }).addTo(live.leaflet);
    }
  }, [runtime, activeLayer, map]);

  /*
   * Entering or leaving the compact layout changes the window, and with it how
   * far out the map has to go before the whole place fits.
   */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;
    const bounds = toLatLngBounds(map.bounds);
    live.leaflet.invalidateSize({ animate: false });
    applyZoomFloor(live.leaflet, map, bounds);
    if (compact) live.leaflet.fitBounds(bounds, { animate: false });
  }, [runtime, compact, map]);

  /*
   * Extract markers.
   *
   * Their own layer group rather than pins: they are reference data, not
   * something anyone placed, so they must never be draggable, selectable, or
   * counted against the room's pin budget.
   */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;

    const group = L.layerGroup();
    if (showExtracts) {
      for (const extract of extractsFor(map.id)) {
        if (!extractFactions.includes(extract.faction)) continue;
        const faction = extract.faction;
        // A transit is a door to another map, not a way out of the raid, and
        // the glyph is the only thing saying so once the name is off screen.
        const glyph = faction === "transit" ? "⇄" : "⇱";
        L.marker([extract.lat, extract.lng], {
          interactive: false,
          keyboard: false,
          zIndexOffset: -400,
          icon: L.divIcon({
            className: "extract-icon",
            html: `<div class="extract extract--${faction}">
              <span class="extract__glyph" aria-hidden="true">${glyph}</span>
              <span class="extract__name">${escapeHtml(extract.name ?? t("extracts.unnamed"))}</span>
            </div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          }),
        }).addTo(group);
      }
      group.addTo(live.leaflet);
    }

    return () => {
      group.remove();
    };
  }, [runtime, map.id, showExtracts, extractFactions]);

  /* ---------------------------------------------------------------- */
  /* Pins                                                              */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;
    const markers = markersRef.current;
    // Strokes are drawn as lines below; a marker at one end of one would be a
    // second thing to click, sitting where nobody put it.
    const visible = pins.filter((pin) => pin.mapId === map.id && pin.kind !== "draw");
    const seen = new Set<string>();

    for (const pin of visible) {
      seen.add(pin.id);
      const isMine = pin.authorId === selfId;
      /*
       * The server lets anyone move anyone's pin except someone else's "self"
       * marker -- the same rule as deleting. Offering a drag the server would
       * silently refuse would just look broken, so the handle matches the rule.
       *
       * Dragging is also off while a placement tool is armed: a press on the
       * map then means "put a new pin here", and the two gestures would fight.
       */
      const canDrag = !placing && (pin.kind !== "self" || isMine);
      const onThisFloor = (pin.layer ?? null) === activeLayer;
      const signature = [
        pin.lat,
        pin.lng,
        pin.heading,
        pin.label,
        pin.color,
        pin.kind,
        pin.expiresAt,
        isMine,
        onThisFloor,
      ].join("|");

      const existing = markers.get(pin.id);
      if (existing) {
        if (existing.signature === signature) continue;
        // Someone else's update must not snatch a marker out of the hand
        // that is currently moving it.
        if (draggingRef.current === pin.id) continue;
        existing.marker.setLatLng([pin.lat, pin.lng]);
        existing.marker.setIcon(pinIcon(pin, isMine));
        existing.marker.setOpacity(onThisFloor ? 1 : 0.35);
        if (existing.marker.dragging) {
          canDrag ? existing.marker.dragging.enable() : existing.marker.dragging.disable();
        }
        existing.signature = signature;
        continue;
      }

      const marker = L.marker([pin.lat, pin.lng], {
        icon: pinIcon(pin, isMine),
        opacity: onThisFloor ? 1 : 0.35,
        // Pins on the current floor sit above the ones on other floors.
        zIndexOffset: onThisFloor ? 500 : 0,
        keyboard: false,
        draggable: canDrag,
        autoPan: true,
      }).addTo(live.leaflet);

      marker.on("dragstart", () => {
        draggingRef.current = pin.id;
      });

      marker.on("dragend", () => {
        draggingRef.current = null;
        const { lat, lng } = marker.getLatLng();
        // Heading is left alone: dragging says where it is, not where it faces.
        onMovePinRef.current(pin.id, lat, lng);
      });

      marker.on("click", (event) => {
        L.DomEvent.stopPropagation(event);
        // Leaflet fires a click at the end of a drag on some platforms; that
        // is a move, not a request to open the card.
        if (draggingRef.current === pin.id) return;
        // Drawing across a marker is drawing, not a request to open its card;
        // rubbing one out is not a request to read it either.
        if (drawingRef.current || erasingRef.current) return;
        const current = pinsRef.current.find((candidate) => candidate.id === pin.id);
        if (current) onSelectPinRef.current(current);
      });

      markers.set(pin.id, { marker, signature });
    }

    for (const [id, entry] of markers) {
      if (!seen.has(id)) {
        stopAutoPan(entry.marker);
        entry.marker.remove();
        markers.delete(id);
      }
    }
  }, [runtime, pins, selfId, activeLayer, map.id, placing]);


  /* ---------------------------------------------------------------- */
  /* Freehand strokes. Drawn as polylines under the markers, because a  */
  /* line is context for the pins rather than something to click first. */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;
    const lines = strokesRef.current;
    const visible = pins.filter(
      (pin) => pin.kind === "draw" && pin.path !== null && pin.mapId === map.id,
    );
    const seen = new Set<string>();

    for (const pin of visible) {
      if (!pin.path) continue;
      seen.add(pin.id);
      const onThisFloor = (pin.layer ?? null) === activeLayer;
      const signature = [pin.color, onThisFloor, pin.path.length].join("|");

      const existing = lines.get(pin.id);
      if (existing) {
        if (existing.signature === signature) continue;
        existing.line.setLatLngs(pin.path);
        existing.hit.setLatLngs(pin.path);
        existing.line.setStyle({ color: pin.color, opacity: onThisFloor ? STROKE_STYLE.opacity : 0.3 });
        existing.signature = signature;
        continue;
      }

      /*
       * The clickable copy goes on first, so it sits under the visible one and
       * cannot smear the colour. It is what people actually hit; the thin line
       * is only what they see.
       */
      const hit = L.polyline(pin.path, {
        weight: STROKE_HIT_WEIGHT,
        opacity: 0,
        lineCap: "round",
        lineJoin: "round",
        smoothFactor: STROKE_STYLE.smoothFactor,
        className: "stroke-hit",
      }).addTo(live.leaflet);

      const line = L.polyline(pin.path, {
        ...STROKE_STYLE,
        color: pin.color,
        opacity: onThisFloor ? STROKE_STYLE.opacity : 0.3,
        className: "stroke",
        // Clicks belong to the wide copy; two overlapping targets would only
        // make the hit depend on which pixel was under the pointer.
        interactive: false,
      }).addTo(live.leaflet);

      hit.on("click", (event) => {
        // While a tool is armed the map belongs to that tool: a press here is a
        // new stroke or a new pin, not a request to open this one.
        if (placingRef.current) return;
        L.DomEvent.stopPropagation(event);
        const current = pinsRef.current.find((candidate) => candidate.id === pin.id);
        if (current) onSelectPinRef.current(current);
      });

      lines.set(pin.id, { line, hit, signature });
    }

    for (const [id, entry] of lines) {
      if (!seen.has(id)) {
        entry.line.remove();
        entry.hit.remove();
        lines.delete(id);
      }
    }

    /*
     * Hand the previews back as the real strokes arrive. Our own strokes come
     * back in the order they were sent, so the oldest preview belongs to the
     * oldest arrival; anything else would need an id the client does not have
     * until the server answers.
     */
    const mine = visible.filter((pin) => pin.authorId === selfId).length;
    for (let i = myStrokeCountRef.current; i < mine; i += 1) {
      const pending = pendingRef.current.shift();
      if (!pending) break;
      window.clearTimeout(pending.timer);
      pending.line.remove();
    }
    myStrokeCountRef.current = mine;
  }, [runtime, pins, selfId, activeLayer, map.id]);

  /* ---------------------------------------------------------------- */
  /* Drawing: press, drag, release. One stroke per gesture.            */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!runtime) return;
    const { leaflet } = runtime;
    const container = leaflet.getContainer();

    let stroke: {
      pointerId: number;
      points: L.LatLng[];
      last: L.Point;
      minStep: number;
      line: L.Polyline;
    } | null = null;

    const discard = () => {
      stroke?.line.remove();
      stroke = null;
    };

    const handleDown = (event: PointerEvent) => {
      if (!drawingRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      /*
       * A second finger means a pinch, not a second line. Leaflet still has its
       * touch zoom, so the gesture people already know keeps working -- what it
       * must not do is leave half a stroke behind on the way.
       */
      if (stroke) {
        discard();
        return;
      }
      event.preventDefault();

      const latlng = leaflet.mouseEventToLatLng(event);
      stroke = {
        pointerId: event.pointerId,
        points: [latlng],
        last: leaflet.mouseEventToContainerPoint(event),
        minStep: STROKE_MIN_STEP,
        line: L.polyline([latlng], {
          ...STROKE_STYLE,
          color: penColorRef.current,
          className: "stroke stroke--live",
        }).addTo(leaflet),
      };

      try {
        container.setPointerCapture(event.pointerId);
      } catch {
        // Capture is a convenience; drawing past the edge still works without it.
      }
    };

    const handleMove = (event: PointerEvent) => {
      if (!stroke || event.pointerId !== stroke.pointerId) return;
      const point = leaflet.mouseEventToContainerPoint(event);
      if (point.distanceTo(stroke.last) < stroke.minStep) return;

      stroke.last = point;
      stroke.points.push(leaflet.containerPointToLatLng(point));

      if (stroke.points.length >= MAX_STROKE_POINTS) {
        /*
         * At the cap, halve what is held and take bigger steps from here. A
         * long sweep then keeps its whole shape at lower detail instead of
         * stopping dead where the budget ran out.
         */
        const last = stroke.points.length - 1;
        stroke.points = stroke.points.filter((_, index) => index % 2 === 0 || index === last);
        stroke.minStep *= 2;
      }

      stroke.line.setLatLngs(stroke.points);
    };

    const finish = (event: PointerEvent) => {
      if (!stroke || event.pointerId !== stroke.pointerId) return;
      const { points, line } = stroke;
      stroke = null;

      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }

      /*
       * A tap with the pen armed is a miss, not a line: nothing to send.
       *
       * It deliberately does nothing else either. Taking something off the map
       * is the rubber's job, and reading one is a click with no tool armed --
       * a third meaning for the same tap, decided by what happens to be under
       * it, is how a drawing tool starts feeling haunted.
       */
      if (points.length < 2) {
        line.remove();
        return;
      }

      /*
       * The line stays where the hand left it until the server sends it back.
       * The timer is only a backstop: if the stroke is ever refused, the map
       * must not keep a line the room does not have.
       */
      const entry = {
        line,
        timer: window.setTimeout(() => {
          line.remove();
          pendingRef.current = pendingRef.current.filter((candidate) => candidate !== entry);
        }, STROKE_ECHO_GRACE_MS),
      };
      pendingRef.current.push(entry);

      onDrawRef.current(points.map((point) => [point.lat, point.lng]));
    };

    const cancel = (event: PointerEvent) => {
      if (!stroke || event.pointerId !== stroke.pointerId) return;
      discard();
    };

    container.addEventListener("pointerdown", handleDown);
    container.addEventListener("pointermove", handleMove);
    container.addEventListener("pointerup", finish);
    container.addEventListener("pointercancel", cancel);

    return () => {
      container.removeEventListener("pointerdown", handleDown);
      container.removeEventListener("pointermove", handleMove);
      container.removeEventListener("pointerup", finish);
      container.removeEventListener("pointercancel", cancel);
      discard();
    };
  }, [runtime]);

  /* ---------------------------------------------------------------- */
  /* Placement: press to set the spot, drag to aim, release to commit.  */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!runtime) return;
    const { leaflet } = runtime;
    const container = leaflet.getContainer();

    const preview = document.createElement("div");
    preview.className = "place-preview";
    container.appendChild(preview);

    let active: {
      pointerId: number;
      startX: number;
      startY: number;
      latlng: L.LatLng;
      heading: number | null;
    } | null = null;

    const showPreview = (point: L.Point) => {
      preview.style.left = `${point.x}px`;
      preview.style.top = `${point.y}px`;
      preview.classList.add("is-visible");
      preview.classList.remove("has-heading");
    };

    const handleDown = (event: PointerEvent) => {
      if (!placingRef.current || drawingRef.current || erasingRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();

      active = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        latlng: leaflet.mouseEventToLatLng(event),
        heading: null,
      };
      try {
        container.setPointerCapture(event.pointerId);
      } catch {
        // Capture is a convenience; dragging past the edge still works without it.
      }
      showPreview(leaflet.mouseEventToContainerPoint(event));
    };

    const handleMove = (event: PointerEvent) => {
      if (!active || event.pointerId !== active.pointerId) return;
      const dx = event.clientX - active.startX;
      const dy = event.clientY - active.startY;

      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD) {
        active.heading = null;
        preview.classList.remove("has-heading");
        return;
      }
      // Screen space: 0 points to the top of the map, growing clockwise.
      const heading = (Math.atan2(dx, -dy) * 180) / Math.PI;
      active.heading = (heading + 360) % 360;
      preview.style.setProperty("--heading", `${active.heading}deg`);
      preview.classList.add("has-heading");
    };

    const finish = (event: PointerEvent) => {
      if (!active || event.pointerId !== active.pointerId) return;
      const { latlng, heading } = active;
      active = null;
      preview.classList.remove("is-visible", "has-heading");
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      onPlaceRef.current({ lat: latlng.lat, lng: latlng.lng, heading });
    };

    const cancel = (event: PointerEvent) => {
      if (!active || event.pointerId !== active.pointerId) return;
      active = null;
      preview.classList.remove("is-visible", "has-heading");
    };

    container.addEventListener("pointerdown", handleDown);
    container.addEventListener("pointermove", handleMove);
    container.addEventListener("pointerup", finish);
    container.addEventListener("pointercancel", cancel);

    return () => {
      container.removeEventListener("pointerdown", handleDown);
      container.removeEventListener("pointermove", handleMove);
      container.removeEventListener("pointerup", finish);
      container.removeEventListener("pointercancel", cancel);
      preview.remove();
    };
  }, [runtime]);

  /*
   * Where people come into the map.
   *
   * One marker per entry, and a circle holding half of that entry's spawn
   * points -- deliberately not all of them. The clouds are wide enough that
   * "everything" would be most of the map on half the maps here, and a shape
   * that big is not a hint, it is a shrug. Where even half is most of the map,
   * the label says so in words and no circle is drawn at all.
   *
   * Non-interactive, like the extracts: this is reference, not something anyone
   * placed, and it must never take a click meant for a pin.
   */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const live = runtime;

    const group = L.layerGroup();
    if (showSpawns) {
      const { entries, spots } = spawnsFor(map.id);
      /* Entries the game only gave a code to get numbered, in the order shown. */
      let unnamed = 0;
      const names = entries.map((name) => name ?? t("spawns.unnamed", { n: ++unnamed }));

      for (const spot of spots) {
        L.circle([spot.lat, spot.lng], {
          radius: spot.radius,
          interactive: false,
          className: "spawn-area",
          color: "#7fb2ff",
          weight: 1,
          opacity: 0.55,
          fillColor: "#7fb2ff",
          fillOpacity: 0.12,
        }).addTo(group);

        // The name goes on the biggest spot of its entry. Twenty-two labels on
        // Streets would bury the map they are drawn on, and a spot the game
        // has no entry for gets no name at all rather than an invented one.
        if (!spot.label || spot.entry === null) continue;
        L.marker([spot.lat, spot.lng], {
          interactive: false,
          keyboard: false,
          zIndexOffset: -500,
          icon: L.divIcon({
            className: "spawn-icon",
            html: `<div class="spawn">
              <span class="spawn__glyph" aria-hidden="true">◆</span>
              <span class="spawn__name">${escapeHtml(names[spot.entry] ?? "")}</span>
            </div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          }),
        }).addTo(group);
      }
      group.addTo(live.leaflet);
    }

    return () => {
      group.remove();
    };
  }, [runtime, map.id, showSpawns]);

  /* ---------------------------------------------------------------- */
  /* The rubber. Press or drag over things to take them off the map.   */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!runtime) return;
    const { leaflet } = runtime;
    const container = leaflet.getContainer();
    /** Ids asked for already, so a drag does not send the same removal twice. */
    let rubbed: Set<string> | null = null;

    /**
     * What is under the pointer, markers before lines.
     *
     * A marker is drawn above the lines, so that is the order the eye reads
     * them in; the rubber has to agree with what it looks like it is touching.
     * Only the dot counts for a marker -- the caption next to it belongs to the
     * label, and rubbing out a pin by brushing its text would be a surprise.
     */
    const under = (event: PointerEvent): string | null => {
      for (const [id, entry] of markersRef.current) {
        const element = entry.marker.getElement();
        const dot = element?.querySelector(".pin__dot") ?? element;
        if (!dot) continue;
        const box = dot.getBoundingClientRect();
        if (
          event.clientX >= box.left &&
          event.clientX <= box.right &&
          event.clientY >= box.top &&
          event.clientY <= box.bottom
        ) {
          return id;
        }
      }
      return strokeUnder(leaflet, strokesRef.current, leaflet.mouseEventToContainerPoint(event));
    };

    const rub = (event: PointerEvent) => {
      if (!rubbed) return;
      const id = under(event);
      if (!id || rubbed.has(id)) return;
      const pin = pinsRef.current.find((candidate) => candidate.id === id);
      // The server keeps someone else's own marker for them; asking would be a
      // no-op, and a rubber that looks like it failed is worse than one that
      // visibly leaves that one alone.
      if (pin && pin.kind === "self" && pin.authorId !== selfId) return;
      rubbed.add(id);
      onEraseRef.current(id);
    };
    const start = (event: PointerEvent) => {
      if (!erasingRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      rubbed = new Set();
      try {
        container.setPointerCapture(event.pointerId);
      } catch {
        // Rubbing past the edge still works without it.
      }
      rub(event);
    };

    const move = (event: PointerEvent) => {
      if (!erasingRef.current) return;
      rub(event);
    };

    const end = (event: PointerEvent) => {
      rubbed = null;
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    container.addEventListener("pointerdown", start);
    container.addEventListener("pointermove", move);
    container.addEventListener("pointerup", end);
    container.addEventListener("pointercancel", end);

    return () => {
      container.removeEventListener("pointerdown", start);
      container.removeEventListener("pointermove", move);
      container.removeEventListener("pointerup", end);
      container.removeEventListener("pointercancel", end);
    };
  }, [runtime, selfId]);

  /*
   * The middle button always pans, whatever is armed.
   *
   * Arming the pen takes the left button away from panning -- that is the
   * whole point of a tool -- and someone drawing a route asked for a way to
   * move the map without disarming it every time. The middle button is free
   * and is what people already reach for elsewhere.
   *
   * Leaflet's own dragging never sees the middle button (it filters on
   * `button === 0`), so this pans by hand. `preventDefault` on the way down is
   * what stops the browser's autoscroll from taking the gesture instead.
   */
  useEffect(() => {
    if (!isCurrent(runtime, generationRef.current)) return;
    const { leaflet } = runtime;
    const container = leaflet.getContainer();
    let pan: { pointerId: number; x: number; y: number } | null = null;

    const start = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || event.button !== 1) return;
      event.preventDefault();
      pan = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      container.classList.add("is-panning");
      try {
        container.setPointerCapture(event.pointerId);
      } catch {
        // Capture is a convenience; panning past the edge still works without it.
      }
    };

    const move = (event: PointerEvent) => {
      if (!pan || event.pointerId !== pan.pointerId) return;
      const dx = pan.x - event.clientX;
      const dy = pan.y - event.clientY;
      if (dx === 0 && dy === 0) return;
      pan.x = event.clientX;
      pan.y = event.clientY;
      leaflet.panBy([dx, dy], { animate: false });
    };

    const end = (event: PointerEvent) => {
      if (!pan || event.pointerId !== pan.pointerId) return;
      pan = null;
      container.classList.remove("is-panning");
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    container.addEventListener("pointerdown", start);
    container.addEventListener("pointermove", move);
    container.addEventListener("pointerup", end);
    container.addEventListener("pointercancel", end);

    return () => {
      container.removeEventListener("pointerdown", start);
      container.removeEventListener("pointermove", move);
      container.removeEventListener("pointerup", end);
      container.removeEventListener("pointercancel", end);
      container.classList.remove("is-panning");
    };
  }, [runtime]);

  /* Arming a tool takes panning away from the pointer. */
  useEffect(() => {
    if (!runtime) return;
    const { leaflet } = runtime;
    const container = leaflet.getContainer();
    if (placing) {
      leaflet.dragging.disable();
      container.classList.add("is-placing");
    } else {
      leaflet.dragging.enable();
      container.classList.remove("is-placing");
    }
    container.classList.toggle("is-drawing", drawing);
    container.classList.toggle("is-erasing", erasing);
  }, [runtime, placing, drawing, erasing]);

  return <div className="map-canvas" ref={containerRef} />;
}
